/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook used to retrieve the Theme Manager Context.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to get access to react base hooks inside custom hook.
 */
import { useEffect } from 'react';

/**
 * Allows for the dispatching of actions to the redux store.
 */
import { useDispatch } from 'react-redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Enables the use of category data loaded from the ServiceStack API.
 */
import { CategoriesSelectors, useCategoriesSelector, CategoriesActions } from 'src/store/reducers/categories';

/**
 * Typings
 */
import { IRequestState } from 'src/store/reducers';

/**
 * Typings
 */
import * as Dtos from '../api/dtos';

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const useCategories = (
    versionId: number
): [
    Dtos.Version | null,
    number[],
    IRequestState
] => {
    const version = useCategoriesSelector(state => CategoriesSelectors.selectCategoriesVersion(state, versionId));
    const view = useCategoriesSelector(state => CategoriesSelectors.selectCategoriesView(state, versionId));
    const loadState = useCategoriesSelector(state => CategoriesSelectors.selectCategoriesLoadState(state, versionId));

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(CategoriesActions.loadByVersionId(versionId));

        return () => {
            dispatch(CategoriesActions.clear(versionId));
        }
    }, [versionId, dispatch]);

    return [
        version,
        view,
        loadState
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Exports - Default
 * ---------------------------------------------------------------------------------
 */

export default useCategories;


// WEBPACK FOOTER //
// ./src/hooks/useCategories.ts